import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  DurationMethodMCMeasurementModeEnum,
  MasteryCriteriaChangeTypeEnum,
} from '../../../../constants/general';
import { goalActions } from '../../../../../../../../../store';
import DurationMCRow from '../common/components/durationMCRow';

import type {
  ISAGDurationMasteryCriteriaItem,
  ITreatmentPlanGoal,
} from '../../../../../../../../../utils/types';
import type { ISAGDurationMastryCriteriaState } from '../common/types/states';

interface ISAGDurationMasteryCriteriaProps {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  isEditable: boolean;
}

function isSAGDurationMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is ISAGDurationMasteryCriteriaItem {
  return Boolean(
    criteria &&
      'acquisition' in criteria &&
      'maintenance_frequency' in criteria &&
      'measurement_mode' in criteria.acquisition &&
      'change_type' in criteria.acquisition,
  );
}

function SAGDurationMasteryCriteriaV2({
  goal,
  goalIndex,
  isEditable,
}: ISAGDurationMasteryCriteriaProps) {
  const dispatch = useDispatch();

  const { mastery_criteria, measurement_units } = goal;
  const durationMasteryCriteria = isSAGDurationMasteryCriteria(mastery_criteria)
    ? mastery_criteria
    : null;
  const durationMeasurementUnits =
    measurement_units && 'duration_unit' in measurement_units ? measurement_units : null;
  const { acquisition, generalization, maintenance, maintenance_frequency } =
    durationMasteryCriteria ?? {};

  const [isAcquisitionLoaded, setIsAcquisitionLoaded] = useState(Boolean(acquisition));

  const [masteryCriteriaState, setMasteryCriteriaMCState] =
    useState<ISAGDurationMastryCriteriaState>({
      acquisition: {
        change_type: acquisition?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: acquisition?.desired_amount ?? null,
        practice_duration_count: acquisition?.practice_duration_count ?? null,
        measurement_mode:
          acquisition?.measurement_mode ?? DurationMethodMCMeasurementModeEnum.AVERAGE,
      },
      generalization: {
        change_type: generalization?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: generalization?.desired_amount ?? null,
        practice_duration_count: generalization?.practice_duration_count ?? null,
        measurement_mode:
          generalization?.measurement_mode ?? DurationMethodMCMeasurementModeEnum.AVERAGE,
      },
      maintenance: {
        change_type: maintenance?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: maintenance?.desired_amount ?? null,
        practice_duration_count: maintenance?.practice_duration_count ?? null,
        measurement_mode:
          maintenance?.measurement_mode ?? DurationMethodMCMeasurementModeEnum.AVERAGE,
      },
    maintenance_frequency: maintenance_frequency ?? null,
    });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (acquisition && !isAcquisitionLoaded) {
      setIsAcquisitionLoaded(true);
    }
  }, [acquisition]);

  useEffect(() => {
    const { mastery_criteria } = goal;
    const durationMasteryCriteria = isSAGDurationMasteryCriteria(mastery_criteria)
      ? mastery_criteria
      : null;
    const { acquisition, generalization, maintenance, maintenance_frequency } =
      durationMasteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setMasteryCriteriaMCState({
      acquisition: {
        change_type: acquisition?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: acquisition?.desired_amount ?? null,
        practice_duration_count: acquisition?.practice_duration_count ?? null,
        measurement_mode:
          acquisition?.measurement_mode ?? DurationMethodMCMeasurementModeEnum.AVERAGE,
      },
      generalization: {
        change_type: generalization?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: generalization?.desired_amount ?? null,
        practice_duration_count: generalization?.practice_duration_count ?? null,
        measurement_mode:
          generalization?.measurement_mode ?? DurationMethodMCMeasurementModeEnum.AVERAGE,
      },
      maintenance: {
        change_type: maintenance?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: maintenance?.desired_amount ?? null,
        practice_duration_count: maintenance?.practice_duration_count ?? null,
        measurement_mode:
          maintenance?.measurement_mode ?? DurationMethodMCMeasurementModeEnum.AVERAGE,
      },
      maintenance_frequency: maintenance_frequency ?? null,
    });
  }, [goal?.is_editing, isAcquisitionLoaded, goal?.id]);

  const handleAcquisitionMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      acquisition: {
        ...prev.acquisition,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleGeneralizationMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      generalization: {
        ...prev.generalization,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleMaintenanceMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      maintenance: {
        ...prev.maintenance,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    if (!isDirty) return;
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: masteryCriteriaState,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  }, [masteryCriteriaState]);

  return (
    <>
      <DurationMCRow
        onUpdate={handleAcquisitionMCState}
        goalIndex={goalIndex}
        title="Acquisition"
        dataTestId="acquisitionMcRow"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)}
        values={masteryCriteriaState.acquisition}
        isEditable={isEditable}
        measurementUnits={durationMeasurementUnits}
      />
      <DurationMCRow
        onUpdate={handleGeneralizationMCState}
        goalIndex={goalIndex}
        title="Generalization"
        dataTestId="generalizationMcRow"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)}
        values={masteryCriteriaState.generalization}
        isEditable={isEditable}
        measurementUnits={durationMeasurementUnits}
      />
      <DurationMCRow
        onUpdate={handleMaintenanceMCState}
        goalIndex={goalIndex}
        title="Maintenance"
        dataTestId="maintenanceMcRow"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)}
        values={masteryCriteriaState.maintenance}
        maintenance_frequency={masteryCriteriaState.maintenance_frequency}
        isEditable={isEditable}
        showFrequency
        updateMaintenanceFrequency={(val) => {
          setMasteryCriteriaMCState((prev) => ({
            ...prev,
            maintenance_frequency: val,
          }));
          setIsDirty(true);
        }}
        measurementUnits={durationMeasurementUnits}
      />
    </>
  );
}

export default SAGDurationMasteryCriteriaV2;
